// Admin FX — the USD→HTG rate the platform books at. Setting a rate writes a
// 'manual' row that's current immediately; new quotes snapshot it, so existing
// requests are never re-priced. Used by quotes, receipts (≈ USD), and payouts.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, TrendingUp, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin } from '../../lib/api';

export function AdminFxPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'fx'], queryFn: admin.fxRates });
  const [rate, setRate] = useState('');

  const setMut = useMutation({
    mutationFn: () => admin.setFxRate(Number(rate)),
    onSuccess: () => { setRate(''); qc.invalidateQueries({ queryKey: ['admin', 'fx'] }); toast.success('Rate updated'); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });

  const current = q.data?.current;

  return (
    <PortalShell nav={ADMIN_NAV} activeId="fx" title="FX rates" subtitle="The USD → HTG rate quotes, receipts, and payouts book at.">
      <div className="space-y-5 max-w-3xl">
        {/* Current rate */}
        <Card className="bg-gradient-to-br from-brand-50 to-amber-50/30 border-brand-100">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-700">Current rate</p>
          {q.isLoading ? (
            <p className="mt-2 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</p>
          ) : (
            <>
              <p className="mt-1 text-[30px] font-bold tracking-tight text-slate-900">1 USD = <span className="text-brand-700">{current?.toFixed(2)}</span> HTG</p>
              <p className="text-[12px] text-slate-500">
                Source: <span className="font-semibold">{q.data?.source}</span>
                {q.data?.effective_at ? ` · since ${new Date(q.data.effective_at).toLocaleString()}` : ' (no manual rate set — using fallback)'}
              </p>
            </>
          )}
        </Card>

        {/* Set rate */}
        <Card className="space-y-3">
          <SectionHeader title="Set a new rate" subtitle="Takes effect immediately for new quotes. Existing requests keep their snapshotted rate." />
          <div className="flex items-end gap-3 flex-wrap">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">1 USD = ? HTG</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number" step="0.01" value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder={current?.toFixed(2)}
                  className="w-40 px-3 py-2 text-[15px] font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
                <span className="text-[13px] text-slate-500">HTG</span>
              </div>
            </label>
            <Button onClick={() => setMut.mutate()} loading={setMut.isPending} disabled={!rate || Number(rate) <= 0} leadingIcon={<Check className="w-3.5 h-3.5" />}>Set rate</Button>
          </div>
          <p className="text-[11px] text-slate-400">A daily market-rate feed can be wired later; until then this manual rate is authoritative.</p>
        </Card>

        {/* History */}
        <Card padding="p-0">
          <div className="p-5 border-b border-slate-100"><SectionHeader title="Rate history" /></div>
          {q.isLoading ? (
            <div className="text-center py-10 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : (q.data?.history ?? []).length === 0 ? (
            <EmptyState icon={TrendingUp} title="No rate history" body="Set a rate above to start the log." />
          ) : (
            <table className="text-[13px] w-full">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr><th className="text-left py-2.5 px-5 font-medium">Rate (HTG/USD)</th><th className="text-left py-2.5 px-3 font-medium">Source</th><th className="text-right py-2.5 px-5 font-medium">Effective</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(q.data?.history ?? []).map((r, i) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-5 font-mono font-semibold text-slate-900">{Number(r.rate).toFixed(2)}{i === 0 && <span className="ml-2 text-[10px] font-sans font-semibold text-emerald-600">CURRENT</span>}</td>
                    <td className="py-2.5 px-3 text-slate-600">{r.source}</td>
                    <td className="py-2.5 px-5 text-right text-slate-500 text-[12px]">{new Date(r.effective_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}
